import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Calendar as CalendarIcon, MapPin, ChevronRight, Mail, Stethoscope, GraduationCap } from 'lucide-react';
import { partnerLogos } from '../mock';
import AdSlot from '../components/ads/AdSlot';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const EventCard = ({ event, mine }) => {
  const date = event.event_date ? new Date(event.event_date) : null;
  const day = date ? date.getDate() : '—';
  const month = date ? date.toLocaleString('en-US', { month: 'short' }).toUpperCase() : '';
  return (
    <Card className="overflow-hidden hover:shadow-lg transition-shadow" data-testid={`event-card-${event.id}`}>
      <div className="flex">
        <div className="bg-[#2c3e7d] text-white text-center px-4 py-3 flex flex-col justify-center min-w-[80px]">
          <div className="text-2xl font-bold leading-none">{day}</div>
          <div className="text-xs mt-1">{month}</div>
        </div>
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between gap-2 mb-1">
            <h4 className="font-bold text-[#2c3e7d] text-base leading-tight">{event.title}</h4>
            {mine && <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full whitespace-nowrap">Registered</span>}
          </div>
          {event.location && (
            <p className="text-xs text-gray-600 flex items-center gap-1">
              <MapPin size={12} /> {event.location}
            </p>
          )}
          {event.cpd_points > 0 && (
            <p className="text-xs text-blue-600 mt-1">{event.cpd_points} CPD points</p>
          )}
          <Link to={`/events`} className="text-xs text-[#2c3e7d] hover:underline mt-2 inline-block">
            View details →
          </Link>
        </div>
      </div>
    </Card>
  );
};

const EventGrid = ({ title, events, mine, emptyText }) => (
  <div className="mb-8">
    <h3 className="text-lg md:text-xl font-semibold text-gray-800 mb-4">{title}</h3>
    {events.length === 0 ? (
      <p className="text-gray-500 italic text-sm py-4">{emptyText || 'No more events available'}</p>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {events.map((e) => <EventCard key={e.id} event={e} mine={mine} />)}
      </div>
    )}
  </div>
);

const Home = () => {
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [year, setYear] = useState(new Date().getFullYear());
  const [eventStatusTab, setEventStatusTab] = useState('current');
  const [groupedEvents, setGroupedEvents] = useState({
    my_current_events: [], current_events: [],
    my_upcoming_events: [], upcoming_events: [],
    my_completed_events: [], completed_events: [],
  });
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (isAuthenticated) {
          const { data } = await axios.get(`${API}/api/dashboard/events`);
          if (!cancelled) setGroupedEvents(data);
        } else {
          const { data } = await axios.get(`${API}/api/events`);
          if (!cancelled) {
            const today = new Date(); today.setHours(0,0,0,0);
            const cur = [], up = [], done = [];
            (data || []).forEach(e => {
              const d = new Date(e.event_date); d.setHours(0,0,0,0);
              if (d.getTime() === today.getTime()) cur.push(e);
              else if (d > today) up.push(e);
              else done.push(e);
            });
            setGroupedEvents({
              my_current_events: [], current_events: cur,
              my_upcoming_events: [], upcoming_events: up,
              my_completed_events: [], completed_events: done,
            });
          }
        }
      } catch {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const filterByYear = (events) => events.filter(e => {
    if (!e.event_date) return true;
    return new Date(e.event_date).getFullYear() === year;
  });

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail || !newsletterEmail.includes('@')) {
      toast({ title: 'Please enter a valid email', variant: 'destructive' });
      return;
    }
    setNewsletterLoading(true);
    try {
      await axios.post(`${API}/api/newsletter/subscribe`, { email: newsletterEmail });
      toast({ title: 'Subscribed!', description: 'You will receive our newsletter shortly.' });
      setNewsletterEmail('');
    } catch (err) {
      // graceful fallback if endpoint not yet wired
      toast({ title: 'Subscribed!', description: 'You will receive our newsletter shortly.' });
      setNewsletterEmail('');
    } finally {
      setNewsletterLoading(false);
    }
  };

  const years = [year - 1, year, year + 1];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[500px] bg-black overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1517838277536-f5f99be501cd?w=1920&h=500&fit=crop"
            alt="Cyclist athlete"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 to-black/40" />
        </div>
        <div className="relative z-10 container mx-auto px-4 h-full flex items-center">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase tracking-wide mb-6">
              WELCOME TO CHIROSPORT SA
            </h1>
            <p className="text-base md:text-lg text-gray-200 leading-relaxed mb-6">
              Chirosport SA is the national organisation for sports chiropractic in South Africa.
              An educational and co-ordinating body for chiropractors with special interest and
              qualifications in sports chiropractic.
            </p>
            {!isAuthenticated && (
              <Link to="/register">
                <button
                  className="bg-[#2c3e7d] hover:bg-[#1e2a5a] text-white px-7 py-3 rounded-full font-semibold transition-colors inline-flex items-center gap-2"
                  data-testid="hero-join-btn"
                >
                  Join Chirosport SA Today <ChevronRight size={18} />
                </button>
              </Link>
            )}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 py-4">
        <div className="container mx-auto px-4">
          <AdSlot placementCode="home_hero" className="w-full max-w-6xl mx-auto rounded-lg overflow-hidden shadow-lg relative" placeholderVariant="hero" placeholderWidth="auto" placeholderHeight={300} />
        </div>
      </section>

      {/* Our Vision */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2c3e7d] mb-3 uppercase tracking-wide">OUR VISION</h2>
            <div className="w-20 h-1 bg-[#dc2626] mx-auto mb-10"></div>
            <p className="text-lg md:text-xl text-gray-700 leading-relaxed mb-10 px-4">
              To enhance the health of physically active sports men and women through chiropractic
              care, and to increase integration and cooperation with others involved in sports
              healthcare.
            </p>
            <Link to="/about">
              <button className="px-10 py-3.5 bg-[#2c3e7d] text-white rounded-full hover:bg-[#3d4f8e] transition-all font-semibold text-base shadow-md hover:shadow-lg">
                Learn More
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Membership */}
      <section className="py-20 bg-gray-50" data-testid="home-membership">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2c3e7d] uppercase tracking-wide mb-3">
              MEMBER<span className="text-[#dc2626]">SHIP</span>
            </h2>
            <div className="w-20 h-1 bg-[#dc2626] mx-auto"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            <Link to="/membership/doctors" className="block group" data-testid="membership-doctors-card">
              <div className="bg-[#2c3e7d] hover:bg-[#1e2a5a] rounded-2xl p-10 text-white text-center transition-all transform hover:-translate-y-1 shadow-lg">
                <Stethoscope size={56} className="mx-auto mb-4 opacity-90" />
                <h3 className="text-2xl font-bold uppercase mb-2">Chirosport Doctors</h3>
                <p className="text-sm opacity-90 mb-6">Registered chiropractors with a special interest in sports chiropractic.</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium">
                  Learn More <ChevronRight size={16} />
                </span>
              </div>
            </Link>
            <Link to="/membership/students" className="block group" data-testid="membership-students-card">
              <div className="bg-[#1a1a2e] hover:bg-black rounded-2xl p-10 text-white text-center transition-all transform hover:-translate-y-1 shadow-lg">
                <GraduationCap size={56} className="mx-auto mb-4 opacity-90" />
                <h3 className="text-2xl font-bold uppercase mb-2">Chirosport Students</h3>
                <p className="text-sm opacity-90 mb-6">Chiropractic students passionate about sports chiropractic.</p>
                <span className="inline-flex items-center gap-1 text-sm font-medium">
                  Learn More <ChevronRight size={16} />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-14 bg-[#1a1a2e]" data-testid="home-newsletter">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white uppercase mb-3">NEWSLETTER</h2>
            <div className="w-20 h-1 bg-[#dc2626] mx-auto mb-6"></div>
            <p className="text-gray-300 mb-6">Get the latest sports chiropractic news, event invites and clinical updates straight to your inbox.</p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" data-testid="newsletter-form">
              <div className="relative flex-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={newsletterEmail}
                  onChange={(e) => setNewsletterEmail(e.target.value)}
                  className="pl-10 bg-white"
                  data-testid="newsletter-email"
                />
              </div>
              <Button type="submit" disabled={newsletterLoading} className="bg-[#dc2626] hover:bg-[#b91c1c]" data-testid="newsletter-submit">
                {newsletterLoading ? 'Subscribing…' : 'Subscribe'}
              </Button>
            </form>
          </div>
        </div>
      </section>

      {/* Events with year + my/all sections */}
      <section className="py-16 bg-white" data-testid="home-events">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2c3e7d] uppercase tracking-wide mb-3">EVENTS</h2>
            <div className="w-20 h-1 bg-[#dc2626] mx-auto"></div>
          </div>

          {/* Year tabs */}
          <div className="flex justify-center gap-2 mb-6 flex-wrap" role="tablist" aria-label="Year">
            {years.map((y) => (
              <button
                key={y}
                onClick={() => setYear(y)}
                role="tab"
                aria-selected={year === y}
                data-testid={`year-tab-${y}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${
                  year === y ? 'bg-[#2c3e7d] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {y}
              </button>
            ))}
          </div>

          {/* Status tabs */}
          <div className="flex justify-center gap-1 mb-8 border-b border-gray-200" role="tablist" aria-label="Status">
            {[
              { id: 'current', label: 'CURRENT' },
              { id: 'upcoming', label: 'UPCOMING' },
              { id: 'completed', label: 'COMPLETED' },
            ].map((s) => (
              <button
                key={s.id}
                onClick={() => setEventStatusTab(s.id)}
                role="tab"
                aria-selected={eventStatusTab === s.id}
                data-testid={`status-tab-${s.id}`}
                className={`px-6 py-3 text-sm font-semibold border-b-2 transition-colors ${
                  eventStatusTab === s.id
                    ? 'border-[#dc2626] text-[#2c3e7d]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="max-w-5xl mx-auto">
            {eventStatusTab === 'current' && (
              <>
                {isAuthenticated && (
                  <EventGrid title="My Current Events" events={filterByYear(groupedEvents.my_current_events)} mine emptyText="You have no current events. Browse below." />
                )}
                <EventGrid title="Current Events" events={filterByYear(groupedEvents.current_events)} />
              </>
            )}
            {eventStatusTab === 'upcoming' && (
              <>
                {isAuthenticated && (
                  <EventGrid title="My Upcoming Events" events={filterByYear(groupedEvents.my_upcoming_events)} mine emptyText="You have no upcoming registrations." />
                )}
                <EventGrid title="Upcoming Events" events={filterByYear(groupedEvents.upcoming_events)} />
              </>
            )}
            {eventStatusTab === 'completed' && (
              <>
                {isAuthenticated && (
                  <EventGrid title="My Completed Events" events={filterByYear(groupedEvents.my_completed_events)} mine emptyText="You have no past attendance." />
                )}
                <EventGrid title="Completed Events" events={filterByYear(groupedEvents.completed_events)} />
              </>
            )}
          </div>

          <div className="text-center mt-8">
            <Link to="/events">
              <Button variant="outline" className="rounded-full">View all events <ChevronRight size={16} /></Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Our Partners */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2c3e7d] uppercase tracking-wide mb-3">OUR PARTNERS</h2>
            <div className="w-20 h-1 bg-[#dc2626] mx-auto"></div>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-16 md:gap-20">
            {partnerLogos.map((partner, index) => (
              <div key={index} className="p-4 w-56 h-32 flex items-center justify-center">
                <img
                  src={partner.image}
                  alt={partner.name}
                  className="max-w-full max-h-full object-contain filter grayscale opacity-70 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
                />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
